import React, { useState, useMemo, useCallback, useEffect, useRef } from "react"
import { Link, useHistory } from "react-router-dom"

import { SearchBar } from "@citydna/common"
import { Box, makeStyles, Theme, IconButton, Divider } from "@material-ui/core"
import { ChevronLeftRounded as ChevronLeftRoundedIcon } from "@material-ui/icons"
import { SettingsRounded as SettingsRoundedIcon } from "@material-ui/icons"

import { useDebounce } from "use-debounce"

import { SidebarHeader } from "../common/SidebarHeader"
import { useData } from "../common/DataProvider"

import { Map } from "./Map"
import { MapLegend } from "./MapLegend"
import { FeatureList } from "./FeatureList"
import { useLayerFeatures } from "./useLayerFeatures"
import { MissingDisplayFieldAlert } from "./MissingDisplayFieldAlert"
import { FeatureFormModal } from "../feature-form/FeatureFormModal"
import { useHeights } from "../common/HeightProvider"
import { useWindowHeight } from "@react-hook/window-size"
import { useMapView } from "../common/MapProvider"
import { parseEsriFields } from "../feature-form/fieldHelpers"
import { ToggleCompletionRenderer } from "./ToggleCompletionRenderer"
import { useEsriModules } from "../common/useEsriModules"
import { AddFeatureButton, NEW_GRAPHIC_OBJECTID } from "./AddFeatureButton"
import mapPin from "./map-pin.png"
import { useDraggableGraphic } from "../common/useDraggableGraphic"
// import { FeatureFilters } from "./FeatureFilters"

/** Scoped @material-ui/core stylesheet */
const useStyles = makeStyles<Theme>((theme) => ({
  sidebar: {
    borderRight: `1px solid ${theme.palette.grey[200]}`,
  },
  searchBar: {
    paddingLeft: theme.spacing(2),
  },
  list: {
    flexGrow: 1,
    overflow: "scroll",
  },
}))

export const FeatureLayerEditor = () => {
  /** Grab data */
  const {
    activeFeatureLayer: [featureLayer],
    activeFeature: [feature],
  } = useData()

  /** Add it to the map */
  const { view } = useMapView()
  useEffect(() => {
    if (view && featureLayer) {
      view.map.add(featureLayer)
      // clean up the layer when a new one becomes available
      return () => {
        view.map.remove(featureLayer)
      }
    }
  }, [view, featureLayer])

  /** Centre the map on any features that become available */
  useEffect(() => {
    if (feature && view) {
      view.goTo(feature)
    }
  }, [feature, view])

  /** Handle navigating to a feature, i.e. on map click or results click */
  const history = useHistory()
  const [Graphic] = useEsriModules<[typeof __esri.Graphic]>(["esri/Graphic"])

  /** Navigates the URL to feature */
  const navigateToFeature = useCallback(
    (feature: __esri.Graphic) => {
      if (featureLayer?.id && feature) {
        /** This will trigger the DataProvider to load the feature */
        history.push(
          `/layers/${featureLayer?.id}/${
            feature.attributes.OBJECTID || feature.attributes.ObjectId
          }`
        )
      }
    },
    [history, featureLayer]
  )

  /** Bind click handler to map */
  const currentEditingGraphicRef = useRef<__esri.Graphic | undefined>()
  useEffect(() => {
    if (featureLayer) {
      view?.on("click", ({ x, y }) => {
        view.hitTest({ x, y }).then(({ results }) => {
          const result = results?.length > 1 ? results[0].graphic : undefined
          if (result?.attributes?.OBJECTID === NEW_GRAPHIC_OBJECTID) {
            // return early if it's the draggable graphic
            return
          } else if (
            result?.attributes.OBJECTID ||
            result?.attributes.ObjectId
          ) {
            // pushes the history to the new graphic ID, which will load the data
            navigateToFeature(result)
          } else {
            // otherwise, close any open modal
            history.push(`/layers/${featureLayer.id}`)
          }
        })
      })
    }
  }, [view, navigateToFeature, featureLayer, history, feature, Graphic])

  /** When the URL changes, i.e. a new feature is loaded add the draggable graphic */
  const [draggableGraphic, setDraggableGraphic] = useState<
    __esri.Graphic | undefined
  >()
  useDraggableGraphic(draggableGraphic)
  useEffect(() => {
    // add the draggable graphic, hiding the original
    if (Graphic && view && feature && featureLayer) {
      // store the graphic ref for cleanup
      currentEditingGraphicRef.current = feature

      // filter out the graphic
      featureLayer.definitionExpression = `Not OBJECTID = '${
        feature.attributes.OBJECTID || feature.attributes.ObjectId
      }'`

      // generate the new draggable graphic, add it to the view.
      const draggableGraphic = new Graphic({
        symbol: {
          // @ts-ignore
          type: "picture-marker",
          url: mapPin,
          width: 36,
          height: 36,
          yoffset: 14,
        },
        geometry: feature.geometry.clone(),
        attributes: {
          OBJECTID: NEW_GRAPHIC_OBJECTID,
        },
      })
      view.graphics.add(draggableGraphic)

      // set it, causing a re-render and attaching the drag hanlders from useDraggableGraphic;
      setDraggableGraphic(draggableGraphic)

      return () => {
        if (featureLayer)
          // reset the definition expression to show the original
          featureLayer.definitionExpression = ""
      }
    }
  }, [Graphic, feature, featureLayer, view])

  /** Before saving, update the feature passed to the form's geometry. */
  const handleBeforeSave = () => {
    if (feature && draggableGraphic) {
      feature.geometry = draggableGraphic.geometry.clone()
    }
  }

  const handleClose = () => {
    // remove the feature ID from the url
    featureLayer && history.push(`/layers/${featureLayer.id}`)

    if (currentEditingGraphicRef.current && draggableGraphic) {
      // remove the temp draggable graphic
      view?.graphics.remove(draggableGraphic)
      setDraggableGraphic(undefined)

      // restore the original graphic's visibility
      currentEditingGraphicRef.current.visible = true
      currentEditingGraphicRef.current = undefined
    }
  }

  /** Handle search query */
  const [value, set] = useState("")
  const [query] = useDebounce(value, 500)

  /** Get all the features from the layer */
  const [featureSet] = useLayerFeatures(featureLayer, {
    where: "OBJECTID>0",
    outFields: ["*"],
  })

  /** Create results based on the query */
  const displayField = featureLayer?.displayField
  const results = useMemo(
    () =>
      query
        ? featureSet?.features.filter((feature) => {
            /** grab the title */
            const title = displayField && feature.attributes[displayField]

            /** If it exist, use that in the search. Otherwise exclude from results. */
            return title
              ? title.toLowerCase().includes(query.toLowerCase())
              : false
          })
        : featureSet?.features,
    [featureSet, query, displayField]
  )

  /** Get scoped CSS classes */
  const classes = useStyles()

  /** Get Heights */
  const [heights] = useHeights()
  const windowHeight = useWindowHeight()

  /** Get schema fields for completion indicators in FeatureList */
  const fields = useMemo(
    () => featureLayer && parseEsriFields(featureLayer?.fields),
    [featureLayer]
  )

  return (
    <>
      <Box flexGrow={1} display="flex">
        <Box
          width={375}
          height={windowHeight - heights.header}
          display="flex"
          flexDirection="column"
          position="relative"
          className={classes.sidebar}
        >
          <SidebarHeader
            // @ts-ignore
            primary={featureLayer?.name || featureLayer?.title}
            secondary="Select a feature to begin editing"
            loading={!featureLayer}
            left={
              <IconButton component={Link} to="/layers">
                <ChevronLeftRoundedIcon />
              </IconButton>
            }
            right={
              <IconButton
                component={Link}
                to={`/layers/${featureLayer?.id}/settings`}
              >
                <SettingsRoundedIcon />
              </IconButton>
            }
          >
            <Box p={2} pt={0}>
              <SearchBar
                className={classes.searchBar}
                value={value}
                onChange={(e) => set(e.target.value)}
                onClear={() => set("")}
              />
            </Box>
            <MissingDisplayFieldAlert layer={featureLayer} />
            <Divider />
            <Box pl={2} display="flex" flexWrap="wrap">
              <ToggleCompletionRenderer />
              {/* <FeatureFilters /> */}
            </Box>
          </SidebarHeader>
          <FeatureList
            features={results}
            displayField={displayField}
            onClick={navigateToFeature}
            fields={fields}
          />
          <AddFeatureButton />
        </Box>
        <Box
          flexGrow={1}
          position="relative"
          height={windowHeight - heights.header}
        >
          <Map />
          <MapLegend />
          {/* This is so that the AddFeatureButton can open a form in the map area */}
          <div id="map-portal-outlet" />
        </Box>
      </Box>
      <FeatureFormModal
        featureLayer={featureLayer}
        feature={feature}
        beforeSave={handleBeforeSave}
        onClose={handleClose}
      />
    </>
  )
}
